import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { ref, get } from 'firebase/database';
import { db, database } from '../firebase';

const ShadowModeContext = createContext();

const STORAGE_KEY = 'vortex_shadow_uid';

export const ShadowModeProvider = ({ children }) => {
  const [shadowUid, setShadowUid] = useState(() => {
    return sessionStorage.getItem(STORAGE_KEY) || null;
  });
  const [shadowUserData, setShadowUserData] = useState(null);
  const [loadingShadow, setLoadingShadow] = useState(false);

  const activateShadow = useCallback(async (uid) => {
    if (!uid) return;
    setLoadingShadow(true);
    sessionStorage.setItem(STORAGE_KEY, uid);
    setShadowUid(uid);

    try {
      const [profileSnap, versionsSnap, usersWebSnap] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        get(ref(database, `userVersions/${uid}`)),
        get(ref(database, `usersweb/${uid}`)),
      ]);

      const data = { uid };
      if (profileSnap.exists()) data.profile = profileSnap.data();
      if (versionsSnap.exists()) data.versions = versionsSnap.val();
      if (usersWebSnap.exists()) data.usersweb = usersWebSnap.val();

      const displayName = data.profile?.displayName || data.profile?.nick || data.usersweb?.displayName || uid;
      const email = data.profile?.email || data.versions?.userEmail || data.usersweb?.email || '';
      data.displayName = displayName;
      data.email = email;

      setShadowUserData(data);
    } catch (err) {
      console.error('[ShadowMode] Error cargando datos del usuario:', err);
    } finally {
      setLoadingShadow(false);
    }
  }, []);

  const deactivateShadow = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setShadowUid(null);
    setShadowUserData(null);
  }, []);

  // Restaurar shadow al recargar página si hay UID en sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && !shadowUserData) {
      activateShadow(stored);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ShadowModeContext.Provider value={{
      shadowUid,
      shadowUserData,
      loadingShadow,
      isShadowActive: !!shadowUid,
      activateShadow,
      deactivateShadow,
    }}>
      {children}
    </ShadowModeContext.Provider>
  );
};

export const useShadowMode = () => {
  const ctx = useContext(ShadowModeContext);
  if (!ctx) throw new Error('useShadowMode debe usarse dentro de ShadowModeProvider');
  return ctx;
};

export default ShadowModeContext;
